import React from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';

interface FormRowProps {
  label: string;
  children: React.ReactNode;
  tooltip?: React.ReactNode; // Optional tooltip like in NewAgent
  /** Marks the field as required in the label, for screen readers too. */
  required?: boolean;
  labelClassName?: string;
  wrapperClassName?: string;
}

const FormRow: React.FC<FormRowProps> = ({
  label,
  children,
  tooltip,
  required = false,
  labelClassName = 'text-lg font-medium text-foreground', // Default style from NewAgent
  wrapperClassName = 'flex flex-col sm:grid sm:grid-cols-[180px_1fr] items-start gap-3 sm:gap-4 bg-transparent px-4 sm:px-6 py-2 rounded-xl', // Mobile-first responsive layout
}) => {
  const { t } = useTranslation();

  return (
    <div className={wrapperClassName}>
      <Label className={labelClassName}>
        {label}
        {/* The asterisk is decoration; `aria-label` on the sibling span is what
            a screen reader announces, so "required" is spoken rather than
            read out as a punctuation mark or skipped entirely. */}
        {required && (
          <>
            <span aria-hidden="true" className="ml-0.5 text-destructive">
              *
            </span>
            <span className="sr-only">
              {' '}
              {t('common.form.required.translation', '(required)')}
            </span>
          </>
        )}
        {tooltip && (
          <span className="ml-2 inline-flex items-center">{tooltip}</span>
        )}{' '}
        {/* Add spacing for tooltip */}
      </Label>
      {/* Render the input component passed as children */}
      <div className="w-full">{children}</div>
    </div>
  );
};

export default FormRow;
